import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AgentConversationFormActions } from '@/agent-v2/chat-form/store/slice'
import { useCreateNewAgentV2Chat } from '@/agent-v2/conversations/hooks/useNewAgentV2Chat'
import { buildConversationPath } from '@/chat-common/conversation-navigation/util/conversation-path-builders'
import { GlobalToastType, kToastDurationMediumMs } from '@/constants/constants-ui'
import { kRouteSegments } from '@/routing/paths'
import { openGlobalToast } from '@/store/slices/global-toast.slice'
import { useAppDispatch } from '@/store/store-hooks'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { UserFilesActions } from '@/user_files/files/store/slice'
import { getEmptyFolderOutcomeMessage } from '@/user_files/utils/empty-folder-outcome-message'
import { resolveSelectedFilesForChat } from '@/user_files/files/utils/resolve-selected-files-for-chat'
import { isAbortError } from '@/util/errors/is-abort-error'

type UseStartChatWithSelectedFilesParams = {
  matterId: string
  /** The file rows currently on screen, used to look the ticked ids up. */
  files: UserFileRead[]
  selectedFileIds: string[]
  selectedFolderIds: string[]
}

// An empty folder and a folder of unprocessed files are different outcomes, and "not ready yet"
// misdescribes the first: there is nothing to wait for. The empty-folder copy is shared with the
// Add-files picker, which resolves the same kind of selection and must not word it differently.
const kNothingAttachableMessage = 'None of the selected files are ready for the Assistant yet.'
const kStartChatFailedMessage = 'Failed to start chat. Please try again.'

/**
 * Starts an Assistant chat from the Matter Files selection.
 *
 * Creates a fresh conversation, seeds its attached files, then navigates to it in the same tab.
 * A *fresh* conversation matters: on open, the form is restored from the conversation's last turn,
 * and only a conversation with no turns leaves the seeded selection alone
 * (`restoreAttachedFilesFromLatestTurn` returns early with no latest turn).
 */
export function useStartChatWithSelectedFiles({ matterId, files, selectedFileIds, selectedFolderIds }: UseStartChatWithSelectedFilesParams) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { createNewAgentV2Chat } = useCreateNewAgentV2Chat()
  const [isStarting, setIsStarting] = useState(false)

  // Folder expansion and conversation creation both outlive a navigation away from the drive. The
  // controller cancels the folder requests, which are the only cancellable half; the ref is what
  // stops everything after an await — toasts, dispatches, the navigate itself — from firing out of a
  // surface that is already gone.
  const abortControllerRef = useRef<AbortController | null>(null)
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      abortControllerRef.current?.abort()
    }
  }, [])

  const startChat = useCallback(async () => {
    if (isStarting) return
    setIsStarting(true)

    const abortController = new AbortController()
    abortControllerRef.current = abortController

    try {
      const selectedFileIdSet = new Set(selectedFileIds)
      const { attachableFileIds, skippedCount, emptyFolderCount } = await resolveSelectedFilesForChat({
        selectedFiles: files.filter((file) => selectedFileIdSet.has(file.id)),
        selectedFolderIds,
        signal: abortController.signal,
        onFolderFilesLoaded: (folderFiles) => dispatch(UserFilesActions.upsertUserFiles(folderFiles)),
      })
      if (!isMountedRef.current) return

      if (attachableFileIds.length === 0) {
        // Nothing skipped means nothing was covered at all — only empty folders were ticked. That is
        // an expected outcome rather than a failure, so it is stated as information; anything the
        // user picked that got dropped is a warning instead.
        const isEmptyFolderOutcome = skippedCount === 0 && emptyFolderCount > 0
        dispatch(
          openGlobalToast({
            type: isEmptyFolderOutcome ? GlobalToastType.INFO : GlobalToastType.WARNING,
            message: isEmptyFolderOutcome ? getEmptyFolderOutcomeMessage(0, emptyFolderCount) : kNothingAttachableMessage,
            durationMs: kToastDurationMediumMs,
          })
        )
        return
      }

      // Not the create endpoints directly: these hooks own both store writes a new conversation
      // needs — the entity upsert *and* the matter's conversation-list reconciliation. Skipping the
      // second lets a list fetch that was already in flight land afterwards and drop the new
      // conversation; the chat would then be missing from chat history for the length of the 60s
      // list cache, because the route's own refresh is not forced.
      const conversation = await createNewAgentV2Chat(false, matterId)
      if (!isMountedRef.current) return

      // Both hooks swallow their own errors and return undefined, so there is nothing left to catch.
      // v2 also returns undefined when a conversation lifecycle reset raced the create; that reads as
      // a failure here, which is the same trade `useStartMedSummaryAssetChat` makes — a reset means
      // the matter changed under the user, and this surface is normally unmounted with it.
      if (!conversation) {
        dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: kStartChatFailedMessage, durationMs: kToastDurationMediumMs }))
        return
      }

      const conversationId = conversation.id
      const conversationPath = buildConversationPath(matterId, conversationId, kRouteSegments.agentV2)

      // Seed before navigating so the chat mounts with its chips already in place.
      dispatch(AgentConversationFormActions.setUserFiles({ conversationId, userFileIds: attachableFileIds }))

      // One toast slot, so these are ordered by how much the user needs them: content they picked
      // that got dropped outranks a folder that simply had nothing in it. An empty folder is still
      // worth stating on a successful send, because it explains the file count they ended up with —
      // the same call the Add-files picker makes.
      if (skippedCount > 0) {
        dispatch(
          openGlobalToast({
            type: GlobalToastType.WARNING,
            message: `${skippedCount} file${skippedCount === 1 ? '' : 's'} could not be added — still processing or failed to process.`,
            durationMs: kToastDurationMediumMs,
          })
        )
      } else if (emptyFolderCount > 0) {
        dispatch(
          openGlobalToast({
            type: GlobalToastType.INFO,
            message: getEmptyFolderOutcomeMessage(attachableFileIds.length, emptyFolderCount),
            durationMs: kToastDurationMediumMs,
          })
        )
      }

      navigate(conversationPath)
    } catch (error) {
      if (isAbortError(error) || !isMountedRef.current) return
      dispatch(openGlobalToast({ type: GlobalToastType.ERROR, message: kStartChatFailedMessage, durationMs: kToastDurationMediumMs }))
      console.error('Error starting Assistant chat from Matter Files selection:', error)
    } finally {
      if (abortControllerRef.current === abortController) abortControllerRef.current = null
      if (isMountedRef.current) setIsStarting(false)
    }
  }, [createNewAgentV2Chat, dispatch, files, isStarting, matterId, navigate, selectedFileIds, selectedFolderIds])

  return { isStarting, startChat }
}
